import React, { createContext, useCallback, useContext, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ConfirmDialog, ContextMenu, Menu } from '@ofself/shell';
import {
    MoreHorizontal, Pencil, Copy, Trash2, Pause, Play, Ban, CalendarPlus, RotateCcw, ArrowUpRight, ArrowRight, Info, LogIn,
    Check, MessageSquare, X, UserMinus, Minus, UserPlus, LogOut, Plug, Settings2, Zap, Upload, Link2, Unlink,
    ArrowRightLeft, ArrowUp, Undo2, KeyRound, Lock, Power, Eye, EyeOff, Clock, Star, ImageOff, BadgeCheck, CreditCard,
} from 'lucide-react';
import { useToast } from '../common';
import ChoiceList from './ChoiceList';
import { SwitchFace } from './SwitchFace';

// ONE ACTION, ONE SHAPE. Every thing you can do to an item is described once:
//
//   { id, label, run, primary?, danger?, disabled?, hint?, confirm?, icon? }
//
// `confirm.typed: 'DELETE'` makes the dialog ask for that word to be typed
// before it acts (for acts that cannot be undone at scale).
//
// `confirm.choices` ({ label, initial, options: [{ value, title, text,
// confirmLabel }] }) makes it a choice between ways of doing the act (the last
// admin leaving: transfer, or close): the confirm button names the one picked,
// and `confirm()` resolves to its value rather than `true`.
//
// and that one array feeds both places an item's actions may stand — the
// inspector's ActionBar and the item's right-click menu — so the two can never
// disagree. `confirm` ({ title, body, label }) routes the action through the one
// are-you-sure dialog first; anything that removes, revokes or overwrites
// carries one. `run` may be async: a throw becomes a toast, never a silent no-op.

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
    const [ask, setAsk] = useState(null);
    const [typed, setTyped] = useState('');
    const [chosen, setChosen] = useState(null);
    const resolver = useRef(null);
    const confirm = useCallback((opts) => new Promise((resolve) => {
        resolver.current = resolve;
        setTyped('');
        setChosen(opts?.choices ? (opts.choices.initial ?? opts.choices.options?.[0]?.value ?? null) : null);
        setAsk(opts);
    }), []);
    const settle = (answer) => {
        if (answer && ask?.typed && typed.trim() !== ask.typed) return; // the word is the key
        if (answer && ask?.choices && !chosen) return;
        resolver.current?.(answer && ask?.choices ? chosen : answer);
        resolver.current = null;
        setAsk(null);
    };
    const option = ask?.choices?.options?.find((o) => o.value === chosen);
    return (
        <ConfirmContext.Provider value={confirm}>
            {children}
            <ConfirmDialog
                open={!!ask}
                title={ask?.title}
                confirmLabel={option?.confirmLabel || ask?.label || 'Confirm'}
                danger={ask?.danger !== false}
                onConfirm={() => settle(true)}
                onCancel={() => settle(false)}
            >
                {ask?.choices && (
                    <ChoiceList
                        className="nx-confirm-choices" label={ask.choices.label || ask.title} value={chosen} onChange={setChosen}
                        options={(ask.choices.options || []).map((o) => ({ value: o.value, label: o.title, hint: o.text }))}
                    />
                )}
                {ask?.body}
                {ask?.typed && (
                    <label className="nx-typed">
                        <span>Type <b>{ask.typed}</b> to confirm</span>
                        <input
                            value={typed}
                            onChange={(e) => setTyped(e.target.value)}
                            autoComplete="off"
                            spellCheck={false}
                            aria-invalid={typed !== '' && typed.trim() !== ask.typed}
                        />
                    </label>
                )}
            </ConfirmDialog>
        </ConfirmContext.Provider>
    );
}

export function useConfirm() {
    const confirm = useContext(ConfirmContext);
    // Outside the provider (a test rendering one page bare) nothing is asked.
    return confirm || (async () => true);
}

// Run an action the one way: ask first when it says so, report a failure.
export function useRunAction() {
    const confirm = useConfirm();
    const toast = useToast();
    return useCallback(async (action) => {
        if (!action || action.disabled) return;
        if (action.confirm) {
            const ok = await confirm({ danger: !!action.danger, label: action.label.replace(/…$/, ''), ...action.confirm });
            if (!ok) return;
        }
        try {
            await action.run?.();
        } catch (e) {
            toast.error(e?.message || `Could not ${action.label.replace(/…$/, '').toLowerCase()}`);
        }
    }, [confirm, toast]);
}

// The order every bar and menu keeps: the primary first, the rest as given,
// anything destructive last.
export function ordered(actions) {
    const live = (actions || []).filter(Boolean).filter((a) => !a.hidden);
    return [
        ...live.filter((a) => a.primary && !a.danger),
        ...live.filter((a) => !a.primary && !a.danger),
        ...live.filter((a) => a.danger),
    ];
}

// The same actions as menu items (right-click, the bar's More).
export function menuItemsOf(actions, run) {
    const list = ordered(actions);
    const items = [];
    list.forEach((a, i) => {
        if (a.danger && i > 0 && !list[i - 1].danger) items.push({ separator: true });
        // a switch, under More, says what pressing it does
        const label = typeof a.toggle === 'boolean' ? (a.toggle ? 'Turn off' : 'Turn on') : a.label;
        items.push({ label, danger: !!a.danger, disabled: !!a.disabled, hint: a.hint, onSelect: () => run(a) });
    });
    return items;
}

// EVERY ACTION WEARS AN ICON, before its label: by its id, else by its label's
// first word, else a plain arrow. An action may name its own (`icon`).
const ICON_BY_ID = {
    edit: Pencil, change: Pencil, fork: Copy, duplicate: Copy, delete: Trash2, trash: Trash2, purge: Trash2,
    pause: Pause, 'pause-all': Pause, resume: Play, 'resume-all': Play, disconnect: Ban, revoke: Ban,
    renew: CalendarPlus, extend: CalendarPlus, again: RotateCcw, restore: RotateCcw, open: ArrowUpRight,
    about: Info, app: ArrowUpRight, grant: ArrowUpRight, go: ArrowUpRight, site: ArrowUpRight, enter: LogIn,
    save: Check, accept: Check, approve: Check, apply: Check, answer: MessageSquare, reject: X, decline: X,
    dismiss: X, cancel: X, remove: UserMinus, detach: Minus, out: Minus, stop: Minus, unfriend: UserMinus,
    unfollow: UserMinus, friend: UserPlus, follow: UserPlus, join: UserPlus, leave: LogOut, connect: Plug,
    setup: Settings2, unconfigure: Settings2, settings: Settings2, run: Zap, push: Upload, relate: Link2,
    unrelate: Unlink, transfer: ArrowRightLeft, promote: ArrowUp, refund: Undo2, rekey: KeyRound,
    backfill: KeyRound, on: Lock, off: Power, review: Eye, later: Clock, default: Star, unphoto: ImageOff,
    end: LogOut, here: LogOut, everywhere: LogOut, down: EyeOff, detect: Clock, matching: Trash2, all: Trash2,
    link: Link2, 'open-link': ArrowUpRight, ratify: BadgeCheck, allow: Plug, card: CreditCard,
};
const ICON_BY_WORD = {
    edit: Pencil, change: Pencil, delete: Trash2, remove: Minus, open: ArrowUpRight, pause: Pause, resume: Play,
    stop: Minus, revoke: Ban, disconnect: Ban, sign: LogOut, leave: LogOut, make: Star, cancel: X, run: Zap,
};
export function actionIcon(a) {
    if (a.icon !== undefined) return a.icon;
    const Icon = ICON_BY_ID[a.id] || ICON_BY_WORD[String(a.label || '').trim().split(/\s+/)[0].toLowerCase()] || ArrowRight;
    return <Icon size={14} strokeWidth={1.8} aria-hidden="true" />;
}

// A LABEL IS NEVER CUT MID-WORD. When any label would not fit its cell (a
// narrow panel: "Edit acces"), the whole bar goes to icons, each action keeping
// its label as its name and its hover line. Measured with the labels shown, on
// every change of the bar's width, so it comes back when there is room again.
function useCompact(key) {
    const ref = useRef(null);
    const [compact, setCompact] = useState(false);
    useLayoutEffect(() => {
        const bar = ref.current;
        if (!bar) return undefined;
        const check = () => {
            bar.classList.remove('is-compact');
            const cut = [...bar.querySelectorAll('.nx-action:not(.nx-action--more)')].some((b) => b.scrollWidth > b.clientWidth + 1);
            bar.classList.toggle('is-compact', cut);
            setCompact(cut);
        };
        check();
        if (typeof window.ResizeObserver !== 'function') return undefined;
        const watch = new window.ResizeObserver(check);
        watch.observe(bar);
        return () => watch.disconnect();
    }, [key]);
    return [ref, compact];
}

// THE ACTION BAR: an item's actions as equal blocks across its panel, in one
// row. Three stand; a fourth and beyond fold into More (⋯), where the
// destructive ones, ordered last, usually land.
export function ActionBar({ actions, max = 3 }) {
    const run = useRunAction();
    const [busy, setBusy] = useState(null);
    const list = ordered(actions);
    const [ref, compact] = useCompact(list.slice(0, max).map((a) => a.label).join('|'));
    if (!list.length) return null;
    const shown = list.length > max ? list.slice(0, max) : list;
    const rest = list.length > max ? list.slice(max) : [];
    const press = async (a) => {
        setBusy(a.id);
        try { await run(a); } finally { setBusy(null); }
    };
    return (
        // equal cells in one row, and More at its end
        <div
            ref={ref} className={`nx-actions${compact ? ' is-compact' : ''}`} role="toolbar"
            style={{ gridTemplateColumns: `repeat(${shown.length}, minmax(0, 1fr))${rest.length ? ' 32px' : ''}` }}
        >
            {shown.map((a) => {
                // A SWITCH IN THE BAR (`toggle`: whether it is on): its word and
                // the one switch's face, the whole cell the press
                if (typeof a.toggle === 'boolean') {
                    const sw = (
                        <button
                            key={a.id}
                            type="button"
                            role="switch"
                            aria-checked={a.toggle}
                            className={`nx-action nx-action--switch${a.toggle ? ' is-on' : ''}`}
                            disabled={!!a.disabled || busy === a.id}
                            aria-label={a.label}
                            title={a.hint || undefined}
                            onClick={() => press(a)}
                        >
                            <span className="nx-action__word">{a.toggle ? 'On' : 'Off'}</span>
                            <SwitchFace on={a.toggle} disabled={!!a.disabled} />
                        </button>
                    );
                    return a.disabled && a.hint ? <span key={a.id} className="nx-action-cell" title={a.hint}>{sw}</span> : sw;
                }
                const button = (
                    <button
                        key={a.id}
                        type="button"
                        className={`nx-action${a.primary ? ' is-primary' : ''}${a.danger ? ' is-danger' : ''}`}
                        disabled={!!a.disabled || busy === a.id}
                        aria-label={a.label}
                        title={a.hint || (compact ? a.label : undefined)}
                        onClick={() => press(a)}
                    >
                        {actionIcon(a)}
                        {a.label}
                    </button>
                );
                // a disabled button takes no pointer, so its hover line would never
                // show: a greyed action wears it on a cell around it instead
                return a.disabled && a.hint
                    ? <span key={a.id} className="nx-action-cell" title={a.hint}>{button}</span>
                    : button;
            })}
            {rest.length > 0 && (
                <Menu
                    items={menuItemsOf(rest, press)}
                    trigger={(
                        <button type="button" className="nx-action nx-action--more" aria-label="More actions">
                            <MoreHorizontal size={15} strokeWidth={1.7} aria-hidden="true" />
                        </button>
                    )}
                />
            )}
        </div>
    );
}

// An item's right-click menu over its cell or row: the same actions, the same order.
export function WithActions({ actions, children, more = false }) {
    const run = useRunAction();
    const items = useMemo(() => menuItemsOf(actions, run), [actions, run]);
    if (!items.length) return children;
    return (
        <ContextMenu
            items={items}
            more={more}
            trigger={more ? children : <div className="nx-slot">{children}</div>}
        />
    );
}
